import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, Input, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import * as echarts from 'echarts';

import { ConfigService } from '../../shared-services/config.service';
import { GlobalFilterService } from '../../shared-services/global-filter-service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { buildCategoryFilterData, matchValue, MatchMode } from '../../utils/filter.utils';

type SeriesMode = 'bar' | 'line';

interface MixedSeriesConfig {
  mode: SeriesMode;
  name: string;
  valueField: string;
  dimensionField: string;
  dimensionValue: string | number;
  yAxisIndex?: number;
}

interface NormalizedInputResult {
  categories: string[];
  recordsByCategory: Record<string, any[]>;
  categoryMetaByCategory: Record<string, { quarter: string; year: string }>;
  isFlatArrayFormat: boolean;
  metaInfo: {
    totalRecords?: number;
    totalPages?: number;
    currentPage?: number;
    pageSize?: number;
  };
}

@Component({
  selector: 'bar-line',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './bar-line.html',
  styleUrls: ['./bar-line.css'],
})
export class BarLine implements AfterViewInit, OnDestroy {
  @Input() data: any;
  metaInfo: { totalRecords?: number; totalPages?: number; currentPage?: number; pageSize?: number } = {};

  protected myChart!: echarts.ECharts;
  private filterSubscription!: Subscription;
  private resizeObserver: ResizeObserver | null = null;
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  protected reflowSub?: Subscription;
  private renderRetryCount = 0;
  private readonly maxRenderRetries = 12;
  private isCtrlPressed = false;
  private selectedCategories: string[] = [];
  private selectedLegends: string[] = [];
  private selectedYAxisValue: { axisIndex: number; value: number } | null = null;
  private allCategories: string[] = [];
  private xAxisYearsRaw: string[] = [];
  private categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
  private enableQuarterYearAxis = false;

  constructor(
    protected configService: ConfigService,
    protected globalFilterService: GlobalFilterService,
    protected chartFilterHandler: ChartFilterHandlerService,
  ) {
    this.filterSubscription = this.globalFilterService.filter$.subscribe((filters) => {
      if (!this.data?.widgetId) return;

      if (!this.chartFilterHandler.shouldReload(filters, this.data.widgetId)) {
        if (filters?.legendSync) {
          this.handleExternalLegendSync(filters);
        }
        return;
      }

      if (filters?.legendSync) {
        this.handleExternalLegendSync(filters);
      }

      if (!filters?.widgetId) {
        this.selectedCategories = [];
      }

      if (!filters?.legendSync) {
        this.loadAndRenderChart(filters);
      }
    });
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.loadAndRenderChart(), 0);
    window.addEventListener('keydown', this.handleKeyDown);
    window.addEventListener('keyup', this.handleKeyUp);

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.myChart?.resize();
    });
  }

  protected loadAndRenderChart(filters?: any): void {
    const widgetId = this.data?.widgetId || '';
    const chartDom = widgetId ? document.getElementById(widgetId) : null;
    if (!chartDom) {
      if (this.renderRetryCount < this.maxRenderRetries) {
        this.renderRetryCount += 1;
        setTimeout(() => this.loadAndRenderChart(filters), 50);
        return;
      }
      console.warn('Chart DOM not found:', this.data?.widgetId);
      return;
    }
    this.renderRetryCount = 0;

    const config = this.data?.chartConfig || {};
    if (config.height) {
      chartDom.style.height = config.height;
    }

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom);
    }
    this.myChart.showLoading();

    if (this.data?.data) {
      this.initChart(this.data.data);
      return;
    }

    if (!this.data?.apiUrl) {
      this.myChart.hideLoading();
      console.warn('No data or apiUrl provided for widget:', this.data?.widgetId);
      return;
    }

    this.configService.getConfig(this.data.apiUrl, this.data, filters).subscribe({
      next: (res) => {
        const chartData = res?.data || res;
        this.initChart(chartData);
      },
      error: (err) => {
        console.error(`[MbarMline] API error for ${this.data?.widgetId}:`, err);
        this.myChart.hideLoading();
      },
    });

    this.addResizeListener();
  }

  private initChart(chartData: any): void {
    if (!chartData) {
      this.myChart?.hideLoading();
      return;
    }

    const config = this.data?.chartConfig || {};
    // Font scale for large / TV screens
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.round(px * fs);
    const normalized = this.normalizeInput(chartData);
    this.metaInfo = normalized.metaInfo;

    const barMetric = this.data?.barValueField || config.barValueField || 'gross_value';
    const rawLineMetric = this.data?.lineValueField ?? config.lineValueField ?? this.data?.doubleyAxisKey ?? config.doubleyAxisKey ?? 'percentage';
    const lineMetric = String(rawLineMetric || '').trim();
    const dimensionField = this.data?.dimensionField || config.dimensionField || 'year';
    const lineSeriesExplicitlyDisabled =
      config.showLines === false ||
      lineMetric.length === 0 ||
      (Array.isArray(config.lineYears) && config.lineYears.length === 0);

    const useSingleMetricSeries =
      normalized.isFlatArrayFormat &&
      (config.useSingleMetricSeries ?? this.data?.useSingleMetricSeries ?? true);

    const barSeries = this.resolveSeriesConfig(
      config.barSeries || [],
      config.barYears,
      'bar',
      barMetric,
      dimensionField,
      config.barNamePrefix || '',
    );
    const lineSeries = lineSeriesExplicitlyDisabled
      ? []
      : this.resolveSeriesConfig(
        config.lineSeries || [],
        config.lineYears,
        'line',
        lineMetric,
        dimensionField,
        config.lineNamePrefix || 'Margin ',
      );

    const fallbackYears = this.resolveUniqueDimensionValues(
      normalized.recordsByCategory,
      dimensionField,
    ).sort((a, b) => Number(b) - Number(a));

    const finalBars = useSingleMetricSeries && !barSeries.length
      ? [
        {
          mode: 'bar' as const,
          name: String(config.barSeriesName || this.data?.barSeriesName || 'Total'),
          valueField: barMetric,
          dimensionField: '__all__',
          dimensionValue: '__all__',
          yAxisIndex: 0,
        },
      ]
      : barSeries.length
        ? barSeries
        : fallbackYears.slice(0, 3).map((year) => ({
          mode: 'bar' as const,
          name: String(year),
          valueField: barMetric,
          dimensionField,
          dimensionValue: year,
          yAxisIndex: 0,
        }));

    const finalLines = lineSeriesExplicitlyDisabled
      ? []
      : useSingleMetricSeries && !lineSeries.length
        ? [
          {
            mode: 'line' as const,
            name: String(config.lineSeriesName || this.data?.lineSeriesName || 'Margin %'),
            valueField: lineMetric,
            dimensionField: '__all__',
            dimensionValue: '__all__',
            yAxisIndex: 1,
          },
        ]
        : lineSeries.length
          ? lineSeries
          : fallbackYears.slice(0, 3).map((year) => ({
            mode: 'line' as const,
            name: `Margin ${year}`,
            valueField: lineMetric,
            dimensionField,
            dimensionValue: year,
            yAxisIndex: 1,
          }));

    const seriesConfig: MixedSeriesConfig[] = [...finalBars, ...finalLines];
    const categories = normalized.categories;
    const hasQuarterYearMeta = categories.some((category) => {
      const meta = normalized.categoryMetaByCategory?.[category];
      return Boolean(meta?.quarter) && Boolean(meta?.year);
    });
    const enableQuarterYearAxis =
      config.enableQuarterYearAxis ?? this.data?.enableQuarterYearAxis ?? this.data?.enableDoubleXAxis ?? hasQuarterYearMeta;
    const quarterAxisValues = categories.map(
      (category) => normalized.categoryMetaByCategory?.[category]?.quarter || category,
    );
    const yearAxisRawValues = categories.map(
      (category) => normalized.categoryMetaByCategory?.[category]?.year || '',
    );
    const yearAxisValues = new Array(yearAxisRawValues.length).fill('');
    for (let start = 0; start < yearAxisRawValues.length;) {
      const year = yearAxisRawValues[start];
      let end = start;
      while (end + 1 < yearAxisRawValues.length && yearAxisRawValues[end + 1] === year) {
        end++;
      }
      if (year) {
        const midpoint = Math.floor((start + end) / 2);
        yearAxisValues[midpoint] = year;
      }
      start = end + 1;
    }
    this.allCategories = categories;
    this.xAxisYearsRaw = yearAxisRawValues;
    this.categoryMetaByCategory = normalized.categoryMetaByCategory || {};
    this.enableQuarterYearAxis = !!enableQuarterYearAxis;

    const colorPalette: string[] =
      config.colors ||
      this.data?.colors ||
      ['#9e9e9e', '#000000', '#e53935', '#8b0000', '#16a34a', '#1e88e5', '#6b7280', '#ef4444'];

    const barScale = Number(config.barScale || this.data?.barScale || 1000000);
    const barSuffix = String(config.barSuffix || this.data?.barSuffix || 'M');
    const lineSuffix = String(config.lineSuffix || this.data?.lineSuffix || '%');
    const lineDecimals = Number(config.lineDecimals ?? this.data?.lineDecimals ?? 0);

    const series = seriesConfig.map((item, idx) => {
      const dataPoints = categories.map((category) => {
        const rows = normalized.recordsByCategory[category] || [];
        const row =
          item.dimensionField === '__all__'
            ? rows[0]
            : rows.find(
              (r: any) => String(r?.[item.dimensionField]) === String(item.dimensionValue),
            );
        const rawValue = Number(row?.[item.valueField] ?? 0);
        const chartValue =
          item.mode === 'bar' ? rawValue / (barScale || 1) : Number(rawValue.toFixed(lineDecimals));

        return {
          value: chartValue,
          rawValue,
          category,
          quarter: normalized.categoryMetaByCategory?.[category]?.quarter,
          year: normalized.categoryMetaByCategory?.[category]?.year,
          [item.dimensionField]: item.dimensionValue,
          metric: item.valueField,
          series: item.name,
        };
      });

      if (item.mode === 'bar') {
        return {
          name: item.name,
          type: 'bar',
          yAxisIndex: item.yAxisIndex ?? 0,
          xAxisIndex: 0,
          itemStyle: { color: colorPalette[idx % colorPalette.length] },
          barMaxWidth: sc(config.barMaxWidth || 32),
          label:
            config.showBarLabels === false
              ? { show: false }
              : {
                show: true,
                position: 'top',
                formatter: (params: any) => `${Math.round(params.value)}${barSuffix}`,
                fontSize: sc(10),
                color: '#444',
              },
          data: dataPoints,
        };
      }

      return {
        name: item.name,
        type: 'line',
        yAxisIndex: item.yAxisIndex ?? 1,
        xAxisIndex: 0,
        itemStyle: { color: colorPalette[idx % colorPalette.length] },
        symbol: config.lineSymbol || 'circle',
        symbolSize: sc(config.lineSymbolSize || 6),
        lineStyle: { width: sc(config.lineWidth || 2) },
        label:
          config.showLineLabels === false
            ? { show: false }
            : {
              show: true,
              position: 'top',
              formatter: (params: any) => `${Number(params.value).toFixed(lineDecimals)}${lineSuffix}`,
              fontSize: sc(10),
              color: '#444',
            },
        data: dataPoints,
      };
    });

    const option: echarts.EChartsCoreOption = {
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' },
        textStyle: { fontSize: sc(12) },
        formatter: (items: any) => {
          const rows = Array.isArray(items) ? items : [items];
          const title = rows[0]?.axisValueLabel || rows[0]?.name || '';
          const body = rows
            .map((point: any) => {
              const rawValue = point?.data?.rawValue ?? point?.value ?? 0;
              const valueText =
                point.seriesType === 'line'
                  ? `${Number(rawValue).toFixed(lineDecimals)}${lineSuffix}`
                  : `${Math.round(Number(rawValue) / (barScale || 1))}${barSuffix}`;
              return `${point.marker} ${point.seriesName}: ${valueText}`;
            })
            .join('<br/>');
          return `<b>${title}</b><br/>${body}`;
        },
      },
      legend: {
        top: sc(4),
        left: 'center',
        type: 'scroll',
        data: seriesConfig.map((s) => s.name),
        textStyle: { fontSize: sc(11) },
        itemWidth: sc(14),
        itemHeight: sc(10),
      },
      grid: {
        top: sc(config.gridTop || 60),
        left: sc(config.gridLeft || 40),
        right: sc(config.gridRight || 48),
        bottom: sc(config.gridBottom || (enableQuarterYearAxis ? 92 : 72)),
        containLabel: true,
      },
      xAxis: enableQuarterYearAxis
        ? [
          {
            type: 'category',
            data: quarterAxisValues,
            triggerEvent: true,
            axisTick: { alignWithLabel: true },
            axisLabel: {
              clickable: true,
              interval: 0,
              rotate: config.xAxisRotate ?? 0,
              fontSize: sc(11),
            },
          },
          {
            type: 'category',
            position: 'bottom',
            offset: sc(config.yearAxisOffset ?? 22),
            data: yearAxisValues,
            triggerEvent: true,
            axisTick: { show: false },
            axisLine: { show: true, lineStyle: { color: '#9ca3af' } },
            axisLabel: {
              clickable: true,
              interval: 0,
              rotate: 0,
              fontSize: sc(11),
              color: config.yearLabelColor || '#374151',
              fontWeight: config.yearLabelFontWeight || 600,
            },
          },
        ]
        : {
          type: 'category',
          data: categories,
          triggerEvent: true,
          axisLabel: {
            clickable: true,
            interval: 0,
            rotate: config.xAxisRotate ?? 45,
            fontSize: sc(11),
          },
        },
      yAxis: [
        {
          type: 'value',
          name: config.leftAxisName || `Sales ($${barSuffix})`,
          nameTextStyle: { fontSize: sc(12) },
          triggerEvent: true,
          min: 0,
          axisLabel: { fontSize: sc(10), formatter: `{value}${barSuffix}` },
          splitLine: { show: false },
        },
        {
          type: 'value',
          name: lineSeriesExplicitlyDisabled ? '' : (config.rightAxisName || 'Margin %'),
          nameTextStyle: { fontSize: sc(12) },
          triggerEvent: true,
          min: config.rightAxisMin ?? 40,
          max: config.rightAxisMax ?? 100,
          axisLabel: { fontSize: sc(10), formatter: `{value}${lineSuffix}` },
          splitLine: { show: false },
          show: !lineSeriesExplicitlyDisabled,
        },
      ],
      series: series as any,
    };

    this.myChart.setOption(option, true);
    this.myChart.hideLoading();
    this.setupChartClickEvent();

    // Re-apply active visual selection after re-render
    if (this.selectedCategories.length > 0) {
      this.highlightCategories(this.selectedCategories);
    } else if (this.selectedLegends.length > 0) {
      this.highlightSeriesByNames(this.selectedLegends);
    }
  }

  private setupChartClickEvent(): void {
    if (!this.myChart) return;
    this.myChart.off('click');
    this.myChart.on('click', (params: any) => {
      // Handle x-axis label clicks
      if (
        params?.componentType === 'xAxis' &&
        (this.data?.chartConfig?.syncXaxisLabels ?? true)
      ) {
        this.handleXAxisLabelClick(params);
        return;
      }

      // Handle y-axis label clicks
      if (params?.componentType === 'yAxis') {
        const axisIndex = params.yAxisIndex ?? 0;
        // Visual highlighting on y-axis click
        if (this.data?.enableYAxisClick || this.data?.chartConfig?.enableYAxisClick) {
          this.handleYAxisLabelClick(params);
          return;
        }
        // Legacy double-axis click filtering to other charts
        if (axisIndex === 1 && this.data?.enableDoubleYAxis && this.data?.enableYAxis2Click) {
          this.chartFilterHandler.handleDoubleYAxisClick(this.data, params.value);
          this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        } else if (axisIndex === 0 && this.data?.enableDoubleXAxis && this.data?.enableXAxis2Click) {
          this.chartFilterHandler.handleDoubleXAxisClick(this.data, params.value);
          this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
        }
        return;
      }

      // Bar/line click → treat as label click (same as Sales by Application)
      if (this.data?.enableLabelClick) {
        const clickedCategory = params?.data?.category ?? params?.name;
        if (clickedCategory) {
          this.applyCategorySelectionWithoutLegendReset(clickedCategory, params);
        }
        return;
      }

      // Fallback: original filter selection behavior
      if (!this.data?.filterKey) return;

      const filterKeys: string[] = Array.isArray(this.data.filterKey)
        ? this.data.filterKey
        : String(this.data.filterKey)
          .split(',')
          .map((k: string) => k.trim())
          .filter(Boolean);

      const payload: any = {};
      const categoryKey = this.data?.categoryKey || this.data?.xAxis || 'category';
      const dimensionField = this.data?.dimensionField || this.data?.chartConfig?.dimensionField || 'year';
      const seriesKey = this.data?.seriesKey || 'series';
      const qtrKeys = new Set(['qtr', 'quarter']);

      filterKeys.forEach((key: string) => {
        if (params?.data && typeof params.data === 'object' && key in params.data) {
          payload[key] = params.data[key];
          return;
        }

        if (key === categoryKey || key === 'category') {
          payload[key] = params?.data?.category ?? params?.name;
          return;
        }

        if (qtrKeys.has(String(key).toLowerCase())) {
          payload[key] = params?.data?.quarter;
          return;
        }

        if (key === dimensionField || key === 'year') {
          payload[key] = params?.data?.year ?? params?.data?.[dimensionField] ?? this.extractYear(params?.seriesName);
          return;
        }

        if (key === seriesKey || key === 'series') {
          payload[key] = params?.seriesName;
        }
      });

      if (!Object.keys(payload).length) return;
      this.chartFilterHandler.updateFilterSelection(this.data, payload);
      this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
    });

    // Handle ECharts built-in legend click for cross-filtering
    this.myChart.off('legendselectchanged');
    this.myChart.on('legendselectchanged', (params: any) => {
      if (!this.data?.enableLegendClick && !this.data?.chartConfig?.enableLegendClick) return;

      const clickedName = params.name;
      const allNames = Object.keys(params.selected);

      // ------------------------------------------------------------------
      // legendClickReverse mode: clicking item X keeps X visible and hides
      // all OTHERS. This reverses the normal ECharts "click to hide" logic.
      // ------------------------------------------------------------------
      if (this.data?.chartConfig?.legendClickReverse) {
        // After ECharts fires, params.selected[clickedName] is already false.
        // Items that are still ON (ECharts didn't touch them):
        const currentlyOn = allNames.filter(k => params.selected[k]);

        let newSelected: Record<string, boolean> = {};
        if (currentlyOn.length === 0) {
          // All are off (user re-clicked the last remaining visible item) → reset to show all
          allNames.forEach(k => newSelected[k] = true);
        } else {
          // Show ONLY the clicked item, hide all others
          allNames.forEach(k => newSelected[k] = k === clickedName);
        }

        this.myChart.setOption({ legend: { selected: newSelected } }, { lazyUpdate: false });
        return; // Skip the standard highlighting logic below
      }

      // ------------------------------------------------------------------
      // Standard mode: prevent ECharts default toggle, then use opacity
      // ------------------------------------------------------------------
      // Prevent ECharts default legend toggle
      const selected: any = {};
      Object.keys(params.selected).forEach(k => selected[k] = true);
      this.myChart.setOption({ legend: { selected } });

      // Multi-select toggle for legend
      if (this.data?.isMultiSelect) {
        const idx = this.selectedLegends.indexOf(clickedName);
        if (idx === -1) {
          this.selectedLegends = [...this.selectedLegends, clickedName];
        } else {
          this.selectedLegends = this.selectedLegends.filter(l => l !== clickedName);
        }
      } else {
        if (this.selectedLegends.length === 1 && this.selectedLegends[0] === clickedName) {
          this.selectedLegends = [];
        } else {
          this.selectedLegends = [clickedName];
        }
      }

      // Visual highlighting
      if (this.selectedLegends.length === 0) {
        this.resetSeriesHighlight();
      } else {
        this.highlightSeriesByNames(this.selectedLegends);
      }

      // Broadcast filter to other charts
      const legendKey = this.data?.legendKey || this.data?.filterKey;
      const listenerConfig =
        this.data?.legendListener ||
        this.data?.filterListener ||
        this.data?.filterListner;

      if (legendKey) {
        if (this.selectedLegends.length > 0) {
          this.globalFilterService.updateFilterSelection(
            legendKey, this.selectedLegends, false, this.data.widgetId, true, listenerConfig,
          );
        } else {
          this.globalFilterService.resetFilterSelection(legendKey, false, listenerConfig);
        }
        this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
      }
    });
  }

  private handleXAxisLabelClick(params: any): void {
    const idx = params?.valueIndex ?? params?.dataIndex;
    if (idx == null || idx < 0) return;

    if (!this.enableQuarterYearAxis) {
      const clickedCategory = this.allCategories[idx] ?? String(params?.value ?? '');
      this.applyCategorySelectionWithoutLegendReset(clickedCategory, params);
      return;
    }

    const axisIndex = params?.axisIndex;
    const rawValue = String(params?.value ?? '').trim();
    const isYearValue = /^(19|20)\d{2}$/.test(rawValue);
    const isQuarterValue = /^Q\s*[1-4]$/i.test(rawValue);

    if (axisIndex === 0 || (axisIndex == null && isQuarterValue)) {
      const fullCategory = this.allCategories[idx];
      this.applyCategorySelectionWithoutLegendReset(fullCategory, params);
      return;
    }

    if (axisIndex === 1 || (axisIndex == null && isYearValue)) {
      const clickedYear =
        this.xAxisYearsRaw[idx] ||
        this.extractYearFromCategoryLocal(this.allCategories[idx]) ||
        rawValue;
      if (!clickedYear) return;

      const nativeEvent = params.event?.event || params.event;
      const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;
      const isMultiSelect = ctrlPressed || this.data?.isMultiSelect === true;

      const selectedYears = Array.from(
        new Set(
          (this.selectedCategories || [])
            .map((c: string) => this.extractYearFromCategoryLocal(c))
            .filter(Boolean),
        ),
      );

      const nextYears = isMultiSelect
        ? selectedYears.includes(clickedYear)
          ? selectedYears.filter((y) => y !== clickedYear)
          : [...selectedYears, clickedYear]
        : selectedYears.length === 1 && selectedYears[0] === clickedYear
          ? []
          : [clickedYear];

      const nextCategories =
        nextYears.length === 0
          ? []
          : this.allCategories.filter((c: string) =>
            nextYears.includes(this.extractYearFromCategoryLocal(c)),
          );

      if (nextCategories.length === 0) {
        this.selectedCategories = [];
        this.resetCategoryHighlight();
        this.broadcastCategoryFilterWithLegendSync([]);
      } else {
        this.selectedCategories = nextCategories;
        this.highlightCategories(nextCategories);
        this.broadcastCategoryFilterWithLegendSync(nextCategories);
      }
      return;
    }

    if (isYearValue) {
      const clickedYear = rawValue;
      const nativeEvent = params.event?.event || params.event;
      const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;
      const isMultiSelect = ctrlPressed || this.data?.isMultiSelect === true;

      const selectedYears = Array.from(
        new Set(
          (this.selectedCategories || [])
            .map((c: string) => this.extractYearFromCategoryLocal(c))
            .filter(Boolean),
        ),
      );

      const nextYears = isMultiSelect
        ? selectedYears.includes(clickedYear)
          ? selectedYears.filter((y) => y !== clickedYear)
          : [...selectedYears, clickedYear]
        : selectedYears.length === 1 && selectedYears[0] === clickedYear
          ? []
          : [clickedYear];

      const nextCategories =
        nextYears.length === 0
          ? []
          : this.allCategories.filter((c: string) =>
            nextYears.includes(this.extractYearFromCategoryLocal(c)),
          );

      if (nextCategories.length === 0) {
        this.selectedCategories = [];
        this.resetCategoryHighlight();
        this.broadcastCategoryFilterWithLegendSync([]);
      } else {
        this.selectedCategories = nextCategories;
        this.highlightCategories(nextCategories);
        this.broadcastCategoryFilterWithLegendSync(nextCategories);
      }
    }
  }

  private handleYAxisLabelClick(params: any): void {
    const axisIndex = params.yAxisIndex ?? 0;
    const clickedValue = Number(params.value);
    if (isNaN(clickedValue)) return;

    // Toggle: if same axis & value clicked again, deselect
    if (
      this.selectedYAxisValue &&
      this.selectedYAxisValue.axisIndex === axisIndex &&
      this.selectedYAxisValue.value === clickedValue
    ) {
      this.selectedYAxisValue = null;
      this.selectedCategories = [];
      this.resetCategoryHighlight();
      this.broadcastCategoryFilterWithLegendSync([]);
      return;
    }

    // Determine which series type to match: left axis (0) → bar, right axis (1) → line
    const targetType = axisIndex === 0 ? 'bar' : 'line';
    const option = this.myChart.getOption() as any;
    const seriesList: any[] = option?.series || [];

    // Compute tick interval from the y-axis to determine match range
    const yAxisOpt = Array.isArray(option?.yAxis) ? option.yAxis[axisIndex] : option?.yAxis;
    const tickInterval = this.getYAxisTickInterval(yAxisOpt, seriesList, targetType);
    const halfInterval = tickInterval / 2;

    // Collect categories whose value falls within [clicked - half, clicked + half)
    const matchingCategories = new Set<string>();
    seriesList.forEach((s: any) => {
      const sType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (sType !== targetType || !Array.isArray(s.data)) return;

      s.data.forEach((d: any) => {
        const val = Number(typeof d === 'object' ? (d?.value ?? d) : d);
        if (!isNaN(val) && val >= clickedValue - halfInterval && val < clickedValue + halfInterval) {
          const category = d?.category;
          if (category) matchingCategories.add(category);
        }
      });
    });

    const matched = Array.from(matchingCategories);
    if (matched.length > 0) {
      this.selectedYAxisValue = { axisIndex, value: clickedValue };
      this.selectedCategories = matched;
      this.highlightCategories(matched);
      this.broadcastCategoryFilterWithLegendSync(matched);
    }
  }

  private getYAxisTickInterval(yAxisOpt: any, seriesList: any[], targetType: string): number {
    // Use explicit interval if set
    const explicit = Array.isArray(yAxisOpt?.interval) ? yAxisOpt.interval[0] : yAxisOpt?.interval;
    if (explicit && Number(explicit) > 0) return Number(explicit);

    // Derive from min/max/splitNumber
    const min = Number(Array.isArray(yAxisOpt?.min) ? yAxisOpt.min[0] : (yAxisOpt?.min ?? 0));
    const max = Number(Array.isArray(yAxisOpt?.max) ? yAxisOpt.max[0] : (yAxisOpt?.max ?? 0));
    if (max > min) {
      const splits = Number(yAxisOpt?.splitNumber ?? 5);
      return (max - min) / splits;
    }

    // Fallback: compute from actual data range
    let dataMin = Infinity;
    let dataMax = -Infinity;
    seriesList.forEach((s: any) => {
      const sType = Array.isArray(s.type) ? s.type[0] : s.type;
      if (sType !== targetType || !Array.isArray(s.data)) return;
      s.data.forEach((d: any) => {
        const val = Number(typeof d === 'object' ? (d?.value ?? d) : d);
        if (!isNaN(val) && isFinite(val)) {
          dataMin = Math.min(dataMin, val);
          dataMax = Math.max(dataMax, val);
        }
      });
    });

    if (isFinite(dataMin) && isFinite(dataMax) && dataMax > dataMin) {
      const splits = Number(yAxisOpt?.splitNumber ?? 5);
      // ECharts expands the range to nice round numbers, approximate by adding ~20%
      const niceRange = (dataMax - Math.min(min || 0, dataMin)) * 1.2;
      return niceRange / splits;
    }

    return 50;
  }

  private updateXAxisFiltersFromSelectedCategories(): void {
    const listener = this.data?.filterListener || this.data?.filterListner;
    const filterKeys: string[] = Array.isArray(this.data?.filterKey)
      ? [...this.data.filterKey]
      : String(this.data?.filterKey || '')
        .split(',')
        .map((k: string) => k.trim())
        .filter(Boolean);

    const includeYear = filterKeys.includes('year');
    const includeQuarter = filterKeys.some((k) => ['quarter', 'qtr'].includes(String(k).toLowerCase()));
    const categoryKey = this.data?.categoryKey || this.data?.xAxis || 'category';
    const includeCategory = filterKeys.includes('category') || filterKeys.includes(categoryKey);

    if (!this.selectedCategories.length) {
      const keysToClear = Array.from(
        new Set([
          ...(includeYear ? ['year'] : []),
          ...(includeQuarter ? ['qtr', 'quarter'] : []),
          ...(includeCategory ? [categoryKey, 'category'] : []),
        ]),
      );
      keysToClear.forEach((key, index) => {
        const isLast = index === keysToClear.length - 1;
        if (isLast) {
          this.globalFilterService.resetFilterSelection(key, false, listener);
        } else {
          this.globalFilterService.deleteKey(key, false);
        }
      });
      return;
    }

    const years = Array.from(
      new Set(
        this.selectedCategories
          .map((cat: string) => this.extractYearFromCategoryLocal(cat))
          .filter(Boolean),
      ),
    );
    const quarters = Array.from(
      new Set(
        this.selectedCategories
          .map((cat: string) => this.extractQuarterFromCategoryLocal(cat))
          .filter(Boolean),
      ),
    ).sort((a, b) => this.getQuarterNum(a) - this.getQuarterNum(b));

    const payload: any = {};
    if (includeCategory) {
      payload[categoryKey] =
        this.selectedCategories.length === 1 ? this.selectedCategories[0] : this.selectedCategories;
    }
    if (includeYear && years.length) {
      payload.year = years.length === 1 ? years[0] : years;
    }
    if (includeQuarter && quarters.length) {
      const quarterKey = filterKeys.includes('quarter') ? 'quarter' : 'qtr';
      payload[quarterKey] = quarters.length === 1 ? quarters[0] : quarters;
    }

    if (!Object.keys(payload).length) return;
    this.chartFilterHandler.updateFilterSelection(this.data, payload);
  }

  private applyCategorySelectionWithoutLegendReset(clickedCategory: any, params: any): void {
    if (!clickedCategory) return;

    const nativeEvent = params.event?.event || params.event;
    const ctrlPressed = this.isCtrlPressed || nativeEvent?.ctrlKey || nativeEvent?.metaKey;
    const isMultiSelect = ctrlPressed || this.data?.isMultiSelect === true;

    if (isMultiSelect) {
      const pos = this.selectedCategories.indexOf(clickedCategory);
      this.selectedCategories =
        pos === -1
          ? [...this.selectedCategories, clickedCategory]
          : this.selectedCategories.filter((c) => c !== clickedCategory);
    } else {
      this.selectedCategories =
        this.selectedCategories.length === 1 && this.selectedCategories[0] === clickedCategory
          ? []
          : [clickedCategory];
    }

    if (this.selectedCategories.length === 0) {
      this.resetCategoryHighlight();
      this.broadcastCategoryFilterWithLegendSync([]);
    } else {
      this.highlightCategories(this.selectedCategories);
      this.broadcastCategoryFilterWithLegendSync(this.selectedCategories);
    }
  }

  // ==================== SERIES HIGHLIGHTING ====================

  private highlightSeriesByNames(names: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      const seriesName = Array.isArray(s.name) ? s.name[0] : s.name;
      const seriesType = Array.isArray(s.type) ? s.type[0] : s.type;
      const isMatch = names.includes(seriesName);
      const opacity = isMatch ? 1 : 0.1;

      if (!Array.isArray(s.data)) {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity } };
      }

      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity },
          label: { ...(val?.label || {}), opacity: isMatch ? 1 : 0 },
        };
      });

      return {
        ...s,
        itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity },
        lineStyle: seriesType === 'line' ? { ...(Array.isArray(s.lineStyle) ? s.lineStyle[0] : s.lineStyle), opacity } : s.lineStyle,
        data: updatedData,
      };
    });

    this.myChart.setOption({ series: updatedSeries }, { lazyUpdate: false });
  }

  private resetSeriesHighlight(): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) {
        return { ...s, itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 } };
      }

      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity: 1 },
          label: { ...(val?.label || {}), opacity: 1 },
        };
      });

      return {
        ...s,
        itemStyle: { ...(Array.isArray(s.itemStyle) ? s.itemStyle[0] : s.itemStyle), opacity: 1 },
        lineStyle: { ...(Array.isArray(s.lineStyle) ? s.lineStyle[0] : s.lineStyle), opacity: 1 },
        data: updatedData,
      };
    });

    this.myChart.setOption({ series: updatedSeries }, { lazyUpdate: false });
  }

  // ==================== CROSS-FILTERING ADDITIONS ====================

  /**
   * Handle incoming legendSync from another chart (visual dimming).
   */
  private handleExternalLegendSync(filters: any): void {
    if (filters.sourceWidgetId === this.data?.widgetId) return;
    if (!this.myChart) return;

    if (filters.selectedLegends?.length === 0 && filters.selectedIndices?.length === 0) {
      this.selectedCategories = [];
      this.resetCategoryHighlight();
      return;
    }

    if (filters.selectedLegends?.length > 0) {
      const matchMode = (this.data?.chartConfig?.categoryMatchMode || 'contains') as MatchMode;
      const matchingCategories = this.allCategories.filter((cat: string) =>
        filters.selectedLegends.some((legend: string) => matchValue(cat, legend, matchMode))
      );
      if (matchingCategories.length > 0) {
        this.selectedCategories = matchingCategories;
        this.highlightCategories(matchingCategories);
      }
    }
  }

  /**
   * Broadcast category selection with legendSync for visual dimming + filter update for API calls.
   */
  private broadcastCategoryFilterWithLegendSync(categories: string[]): void {
    const listenerConfig =
      this.data?.labelListener ||
      this.data?.legendListener ||
      this.data?.filterListener ||
      this.data?.filterListner;

    const labelKey = this.data?.labelKey || this.data?.filterKey;

    // Step 1: legendSync for visual dimming on other charts
    const legendSyncData = {
      ...this.globalFilterService.getCurrentFilters(),
      ...buildCategoryFilterData(categories, this.data.widgetId, listenerConfig, labelKey),
    };
    (this.globalFilterService as any).filterSubject.next(legendSyncData);

    // Step 2: regular filter update for API calls
    this.updateXAxisFiltersFromSelectedCategories();

    // Step 3: reload self
    this.loadAndRenderChart(this.globalFilterService.getCurrentFilters());
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = true;
    }
  };

  private handleKeyUp = (event: KeyboardEvent): void => {
    if (event.key === 'Control' || event.key === 'Meta') {
      this.isCtrlPressed = false;
    }
  };

  private extractYear(input: any): string {
    const match = String(input || '').match(/(19|20)\d{2}/);
    return match ? match[0] : '';
  }

  private extractYearFromCategoryLocal(cat: string): string {
    const m = String(cat).match(/(19|20)\d{2}/);
    return m ? m[0] : '';
  }

  private extractQuarterFromCategoryLocal(cat: string): string {
    const str = String(cat ?? '').toUpperCase();
    const qMatch = str.match(/\bQ\s*([1-4])\b/);
    if (qMatch) return `Q${qMatch[1]}`;

    const quarterWordMatch = str.match(/\bQUARTER\s*([1-4])\b/);
    if (quarterWordMatch) return `Q${quarterWordMatch[1]}`;

    const qtrMatch = str.match(/\bQTR\s*([1-4])\b/);
    if (qtrMatch) return `Q${qtrMatch[1]}`;

    return '';
  }

  private getQuarterNum(q: string): number {
    const m = String(q || '').match(/Q(\d)/i);
    return m ? Number(m[1]) : 0;
  }

  private normalizeInput(raw: any): NormalizedInputResult {
    const config = this.data?.chartConfig || {};
    const enableFlatArrayResponse =
      (config.enableFlatArrayResponse ?? this.data?.enableFlatArrayResponse) === true;
    const dataKey = this.data?.dataKey || config.dataKey;
    const categoryFields: string[] = this.data?.categoryFields || config.categoryFields || ['quarter', 'year'];
    const categoryDelimiter: string = this.data?.categoryDelimiter ?? config.categoryDelimiter ?? ' ';
    const quarterField = this.data?.quarterField || config.quarterField || 'quarter';
    const doublexKey = this.data?.doublexAxisKey || config.doublexAxisKey || '';
    const yearField = this.data?.yearField || config.yearField || (doublexKey ? doublexKey.toLowerCase() : 'year');

    const data = raw?.data || raw;
    const metaInfo = {
      totalRecords: Number(data?.totalRecords),
      totalPages: Number(data?.totalPages),
      currentPage: Number(data?.currentPage),
      pageSize: Number(data?.pageSize),
    };

    const canParseFlatArray =
      enableFlatArrayResponse ||
      Array.isArray(data) ||
      (Boolean(dataKey) && Array.isArray(data?.[dataKey]));

    if (canParseFlatArray) {
      const flatData: any[] = Array.isArray(data)
        ? data
        : dataKey && Array.isArray(data?.[dataKey])
          ? data[dataKey]
          : [];

      if (flatData.length > 0) {
        const buildCategory = (row: any): string =>
          categoryFields.map((field) => String(row?.[field] ?? '')).join(categoryDelimiter).trim();

        const quarterOrder = (value: any): number => {
          const match = String(value ?? '').toUpperCase().match(/Q\s*([1-4])/);
          return match ? Number(match[1]) : 0;
        };

        const uniqueCategories = Array.from(new Set(flatData.map((row: any) => buildCategory(row))));
        const categories = uniqueCategories.sort((a: string, b: string) => {
          const rowA = flatData.find((row: any) => buildCategory(row) === a) || {};
          const rowB = flatData.find((row: any) => buildCategory(row) === b) || {};
          const yearA = Number(rowA?.[yearField] ?? 0);
          const yearB = Number(rowB?.[yearField] ?? 0);
          if (yearA !== yearB) return yearA - yearB;
          return quarterOrder(rowA?.[quarterField]) - quarterOrder(rowB?.[quarterField]);
        });

        const recordsByCategory: Record<string, any[]> = {};
        const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
        categories.forEach((category) => {
          const rows = flatData.filter((row: any) => buildCategory(row) === category);
          recordsByCategory[category] = rows;
          const firstRow = rows[0] || {};
          categoryMetaByCategory[category] = {
            quarter: String(firstRow?.[quarterField] ?? ''),
            year: String(firstRow?.[yearField] ?? ''),
          };
        });

        return {
          categories,
          recordsByCategory,
          categoryMetaByCategory,
          isFlatArrayFormat: true,
          metaInfo,
        };
      }
    }

    if (data && typeof data === 'object' && !Array.isArray(data) && !data.category && !data.series) {
      const categories = Object.keys(data).filter((key) => Array.isArray(data[key]));
      const recordsByCategory: Record<string, any[]> = {};
      const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
      categories.forEach((cat) => {
        recordsByCategory[cat] = Array.isArray(data[cat]) ? data[cat] : [];
        const matchYear = String(cat).match(/(19|20)\d{2}/);
        const matchQuarter = String(cat).match(/Q\s*[1-4]/i);
        categoryMetaByCategory[cat] = {
          quarter: matchQuarter ? matchQuarter[0].replace(/\s+/g, '').toUpperCase() : '',
          year: matchYear ? matchYear[0] : '',
        };
      });
      return { categories, recordsByCategory, categoryMetaByCategory, isFlatArrayFormat: false, metaInfo };
    }

    if (data?.category && data?.series) {
      const categories = Array.isArray(data.category) ? data.category : [];
      const recordsByCategory: Record<string, any[]> = {};
      const categoryMetaByCategory: Record<string, { quarter: string; year: string }> = {};
      categories.forEach((cat: string, idx: number) => {
        recordsByCategory[cat] = (data.series || []).map((series: any) => ({
          year: series?.name,
          value: Array.isArray(series?.data) ? series.data[idx] : 0,
        }));
        const matchYear = String(cat).match(/(19|20)\d{2}/);
        const matchQuarter = String(cat).match(/Q\s*[1-4]/i);
        categoryMetaByCategory[cat] = {
          quarter: matchQuarter ? matchQuarter[0].replace(/\s+/g, '').toUpperCase() : '',
          year: matchYear ? matchYear[0] : '',
        };
      });
      return { categories, recordsByCategory, categoryMetaByCategory, isFlatArrayFormat: false, metaInfo };
    }

    return {
      categories: [],
      recordsByCategory: {},
      categoryMetaByCategory: {},
      isFlatArrayFormat: false,
      metaInfo,
    };
  }

  private resolveSeriesConfig(
    explicitSeries: any[],
    yearList: Array<string | number>,
    mode: SeriesMode,
    defaultValueField: string,
    defaultDimensionField: string,
    namePrefix: string,
  ): MixedSeriesConfig[] {
    if (Array.isArray(explicitSeries) && explicitSeries.length > 0) {
      return explicitSeries.map((item: any) => ({
        mode,
        name: String(item?.name || `${namePrefix}${item?.dimensionValue ?? ''}`),
        valueField: String(item?.valueField || defaultValueField),
        dimensionField: String(item?.dimensionField || defaultDimensionField),
        dimensionValue: item?.dimensionValue,
        yAxisIndex: mode === 'bar' ? 0 : 1,
      }));
    }

    if (Array.isArray(yearList) && yearList.length > 0) {
      return yearList.map((year) => ({
        mode,
        name: mode === 'bar' ? String(year) : `${namePrefix}${year}`,
        valueField: defaultValueField,
        dimensionField: defaultDimensionField,
        dimensionValue: year,
        yAxisIndex: mode === 'bar' ? 0 : 1,
      }));
    }

    return [];
  }

  private resolveUniqueDimensionValues(
    recordsByCategory: Record<string, any[]>,
    dimensionField: string,
  ): Array<string | number> {
    const values = new Set<string | number>();
    Object.values(recordsByCategory).forEach((rows: any[]) => {
      rows.forEach((row: any) => {
        if (row?.[dimensionField] !== undefined && row?.[dimensionField] !== null) {
          values.add(row[dimensionField]);
        }
      });
    });
    return Array.from(values);
  }

  private addResizeListener(): void {
    const chartDom = document.getElementById(this.data?.widgetId || '');
    if (!chartDom) return;

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    this.resizeObserver = new ResizeObserver(() => this.myChart?.resize());
    this.resizeObserver.observe(chartDom);
    window.addEventListener('resize', this.handleResize);
  }

  private handleResize = (): void => {
    this.myChart?.resize();
  };

  private highlightCategories(categories: string[]): void {
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const categoryIndices = categories
      .map((cat: string) => this.allCategories.indexOf(cat))
      .filter((i: number) => i !== -1);

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;

      const updatedData = s.data.map((val: any, i: number) => {
        const isSelected = categoryIndices.includes(i);
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity: isSelected ? 1 : 0.08 },
          label: { ...(val?.label || {}), opacity: isSelected ? 1 : 0 },
        };
      });

      return { ...s, data: updatedData };
    });

    this.myChart.setOption({ series: updatedSeries }, { lazyUpdate: false });
  }

  private resetCategoryHighlight(): void {
    this.selectedCategories = [];
    if (!this.myChart) return;
    const option = this.myChart.getOption() as any;
    if (!option?.series) return;

    const updatedSeries = option.series.map((s: any) => {
      if (!Array.isArray(s.data)) return s;

      const updatedData = s.data.map((val: any) => {
        const rawValue = typeof val === 'object' ? (val?.value ?? val) : val;
        return {
          ...(typeof val === 'object' ? val : {}),
          value: rawValue,
          itemStyle: { ...(val?.itemStyle || {}), opacity: 1 },
          label: { ...(val?.label || {}), opacity: 1 },
        };
      });

      return { ...s, data: updatedData };
    });

    this.myChart.setOption({ series: updatedSeries }, { lazyUpdate: false });
  }

  hasMetaValue(value: any): boolean {
    return value !== undefined && value !== null && Number.isFinite(Number(value));
  }

  /**
   * Resets all local selections and visual highlights.
   */
  public reset(): void {
    this.selectedCategories = [];
    this.selectedLegends = [];
    this.selectedYAxisValue = null;
    this.resetCategoryHighlight();
    this.resetSeriesHighlight();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.reflowSub?.unsubscribe();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('keydown', this.handleKeyDown);
    window.removeEventListener('keyup', this.handleKeyUp);
    this.myChart?.dispose();
  }
}
